import type { StateStorage } from "zustand/middleware";
import { getStorageSessionId } from "@/src/util/storage-session";

type PersistEnvelope = {
  state?: unknown;
  version?: number;
  __storageSession?: string;
};

const parseEnvelope = (raw: string): PersistEnvelope | null => {
  try {
    return JSON.parse(raw) as PersistEnvelope;
  } catch {
    return null;
  }
};

const isStaleEnvelope = (envelope: PersistEnvelope): boolean => {
  const currentSession = getStorageSessionId();
  if (!currentSession) {
    return false;
  }
  if (!envelope.__storageSession) {
    return true;
  }
  return envelope.__storageSession !== currentSession;
};

/** Tags zustand persist payloads with the active browser session id. */
export function createSessionScopedStorage(base: StateStorage): StateStorage {
  return {
    getItem: async (name) => {
      const raw = await base.getItem(name);
      if (!raw) {
        return null;
      }
      const envelope = parseEnvelope(raw);
      if (!envelope) {
        return raw;
      }
      if (isStaleEnvelope(envelope)) {
        return null;
      }
      return JSON.stringify({
        state: envelope.state,
        version: envelope.version ?? 0,
      });
    },
    setItem: async (name, value) => {
      const envelope = parseEnvelope(value);
      if (!envelope) {
        await base.setItem(name, value);
        return;
      }
      const sessionId = getStorageSessionId();
      await base.setItem(
        name,
        JSON.stringify({
          ...envelope,
          __storageSession: sessionId ?? undefined,
        }),
      );
    },
    removeItem: async (name) => {
      await base.removeItem(name);
    },
  };
}
